import { useRef, useState } from "react"
import { Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { AccountSwitcher } from "@/components/AccountSwitcher"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Logo } from "@/components/Logo"
import {
  LayoutDashboard, Users, Settings, Package, Cylinder, Menu,
  Building2, Truck, Tag, Workflow, Beer, FileText, ScanLine, MapPin, Bell, ChevronDown,
  Smartphone,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import type { User, Account } from "@/types"

interface AppLayoutProps {
  children: React.ReactNode
  user: User
  accounts: Account[]
  currentAccountId: number | null
  role?: string | null
}

interface NavItem {
  href: string
  labelKey: string
  icon: LucideIcon
  admin?: boolean
}

const NAV: NavItem[] = [
  { href: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
  { href: "/scan", labelKey: "nav.scan", icon: ScanLine },
  { href: "/inventory", labelKey: "nav.inventory", icon: Package },
  { href: "/contracts/new", labelKey: "nav.contracts", icon: FileText },
  { href: "/stages", labelKey: "nav.stages", icon: Workflow, admin: true },
  { href: "/tags", labelKey: "nav.tags", icon: Tag, admin: true },
  { href: "/products", labelKey: "nav.products", icon: Beer, admin: true },
  { href: "/containers", labelKey: "nav.containers", icon: Cylinder, admin: true },
  { href: "/customers", labelKey: "nav.customers", icon: Building2, admin: true },
  { href: "/suppliers", labelKey: "nav.suppliers", icon: Truck, admin: true },
  { href: "/locations", labelKey: "nav.locations", icon: MapPin, admin: true },
  { href: "/users", labelKey: "nav.users", icon: Users, admin: true },
  { href: "/settings", labelKey: "nav.settings", icon: Settings, admin: true },
]

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5">
      <Logo variant="light" size={34} />
      <div
        className="font-bold text-[16px] uppercase"
        style={{
          fontFamily: '"Space Grotesk", system-ui, sans-serif',
          letterSpacing: "0.02em",
        }}
      >
        BierTrack
      </div>
    </div>
  )
}

// True only for phone browsers where the user picked "use desktop version"
// on the mobile Profile screen. Both cookies are set client-side (see the
// inline script in app/views/layouts/application.html.erb and
// components/mobile/screens/Profile.tsx).
function optedOutOfMobile(): boolean {
  if (typeof document === "undefined") return false
  return (
    /(?:^|;\s*)mobile_web=1(?:;|$)/.test(document.cookie) &&
    /(?:^|;\s*)prefer_desktop=1(?:;|$)/.test(document.cookie)
  )
}

// Clear the desktop preference and return to the mobile app.
function backToMobile() {
  document.cookie = "prefer_desktop=; path=/; max-age=0; SameSite=Lax"
  window.location.assign("/m")
}

export function AppLayout({ children, user, accounts, currentAccountId, role }: AppLayoutProps) {
  const { t } = useTranslation()
  const { url } = usePage()
  const isAdmin = role === "admin"
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const currentAccount = accounts.find(a => a.id === currentAccountId)
  const logoutFormRef = useRef<HTMLFormElement>(null)
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  const showBackToMobile = optedOutOfMobile()

  const isActive = (href: string) => {
    if (href === "/dashboard") {
      return url === "/dashboard" || url === "/dashboard/"
    }
    return url.startsWith(href)
  }

  const items = NAV.filter(item => !item.admin || isAdmin)

  const NavLinks = ({ onNavigate }: { onNavigate?: () => void }) => (
    <>
      {items.map((item) => {
        const active = isActive(item.href)
        const Icon = item.icon
        return (
          <Link
            key={item.href}
            href={item.href}
            onClick={onNavigate}
            className={`relative flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors ${
              active
                ? "font-semibold text-[var(--sidebar-accent-foreground)] bg-[var(--sidebar-accent)]"
                : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)]"
            }`}
          >
            {active && (
              <span
                aria-hidden
                className="absolute left-[-12px] top-1.5 bottom-1.5 w-[3px] rounded-r"
                style={{ background: "var(--sidebar-accent-foreground)" }}
              />
            )}
            <Icon className="w-[17px] h-[17px]" />
            {t(item.labelKey)}
          </Link>
        )
      })}
    </>
  )

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - hidden on mobile */}
      <aside
        className="fixed inset-y-0 left-0 w-60 hidden md:flex md:flex-col"
        style={{
          background: "var(--sidebar)",
          color: "var(--sidebar-foreground)",
          borderRight: "1px solid var(--sidebar-border)",
          zIndex: 10,
        }}
      >
        <div className="px-5 py-[18px]" style={{ borderBottom: "1px solid var(--sidebar-border)" }}>
          <Link href="/dashboard" className="hover:opacity-90 transition-opacity">
            <BrandMark />
          </Link>
        </div>
        <nav className="px-3 py-3.5 flex-1 overflow-y-auto space-y-0.5">
          <NavLinks />
        </nav>
      </aside>

      {/* Mobile Navigation Drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="left"
          style={{
            background: "var(--sidebar)",
            color: "var(--sidebar-foreground)",
            borderRight: "1px solid var(--sidebar-border)",
          }}
        >
          <SheetHeader>
            <SheetTitle asChild>
              <Link href="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                <BrandMark />
              </Link>
            </SheetTitle>
          </SheetHeader>
          <nav className="mt-4 px-3 space-y-0.5">
            <NavLinks onNavigate={() => setMobileMenuOpen(false)} />
          </nav>
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className="md:ml-60">
        {/* Header */}
        <header
          className="sticky top-0 z-[8] h-[58px] px-6 flex justify-between items-center"
          style={{
            background: "color-mix(in oklab, var(--card) 94%, transparent)",
            backdropFilter: "blur(8px)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Open menu</span>
          </Button>
          <div className="hidden md:flex md:items-center md:gap-3">
            <span
              className="font-mono text-[10.5px] uppercase opacity-70"
              style={{ letterSpacing: "0.18em" }}
            >
              Conta
            </span>
            <div
              className="font-display text-[17px] font-semibold uppercase"
              style={{ letterSpacing: "0.02em" }}
            >
              {currentAccount?.name}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {showBackToMobile && (
              <button
                onClick={backToMobile}
                title={t("nav.backToMobile")}
                className="grid place-items-center w-[34px] h-[34px] rounded-[2px] border bg-transparent text-muted-foreground hover:bg-secondary transition-colors"
              >
                <Smartphone className="h-4 w-4" />
                <span className="sr-only">{t("nav.backToMobile")}</span>
              </button>
            )}
            {accounts.length > 0 && (
              <AccountSwitcher
                accounts={accounts}
                currentAccountId={currentAccountId}
              />
            )}
            <button
              title={t("nav.notifications", { defaultValue: "Notificações" })}
              className="hidden sm:grid place-items-center w-[34px] h-[34px] rounded-[2px] border bg-transparent text-muted-foreground hover:bg-secondary transition-colors"
            >
              <Bell className="h-4 w-4" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-[2px] border border-border bg-transparent hover:bg-secondary transition-colors">
                  <span
                    className="grid place-items-center w-7 h-7 rounded-[2px] font-mono font-semibold text-xs"
                    style={{ background: "var(--accent)", color: "var(--accent-foreground)" }}
                  >
                    {(user.name || user.email || "?")[0]?.toUpperCase()}
                  </span>
                  <span className="text-[13px] font-medium">
                    {user.name || user.email}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <a href="/change-password">{t("nav.changePassword")}</a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logoutFormRef.current?.submit()}
                  className="cursor-pointer"
                >
                  {t("nav.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <form ref={logoutFormRef} method="post" action="/logout" className="hidden">
              <input type="hidden" name="authenticity_token" value={csrfToken} />
            </form>
          </div>
        </header>

        <main className="p-6 md:p-7">{children}</main>
      </div>
    </div>
  )
}
